/**
 * Citation Verification Tool - Main Command
 * 
 * Self-contained command for verifying citations in academic manuscripts
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { createFileLogger } from '../../../utils/logger';
import { traceService } from '../../../services/debug/traceService';
import { createCancellable, CancellationError } from '../../../utils/cancellation';

// Import verification modules
import { extractCitations } from './extractor';
import { fetchAndImportPapers } from './fetcher';
import { verifyCitations } from './verifier';
import { createVerificationComments } from './commenter';
import { generateReport } from './reporter';
import { parseCitationScope } from './utils';
import { VerifyCitationsParams, VerificationProgress, ResolutionResult } from './types';

const log = createFileLogger('VERIFY_CITATIONS');

/**
 * Citation Verification Command
 */
export const verifyCitationsCommand: SelfContainedCommand = {
  id: 'verify_citations',
  description: `Verify that citations in a manuscript actually support the claims being made.
  
  Analyzes a document to:
  - Extract all citations and references
  - Resolve references to real publications
  - Fetch cited papers (searching local dataspace first)
  - Verify each claim is supported by the cited source
  - Flag overstatements, contradictions, and missing support
  - Generate detailed verification report
  
  **Best for:** Academic papers, research manuscripts, literature reviews`,
  
  version: 'v1',
  autonomyLevel: 'auto',
  
  systemPromptAddition: `
When using verify_citations:
- Simply call it with the manuscript_id - it will read the document and process it internally
- The tool will extract citations, resolve references, and verify claims automatically
- It reports progress as it works through each phase

The tool verifies:
1. Citation-reference alignment (do in-text citations match the reference list?)
2. Reference metadata accuracy (DOI, title, authors, year)
3. Claim-evidence alignment (do cited claims match what the paper actually says?)
4. Overstatement detection (is language too strong for the evidence?)

It will flag issues like:
- Citations with no matching reference
- References never cited
- Mismatched metadata
- Overclaimed findings
- Retracted papers
`,
  
  schema: {
    parameters: [
      {
        name: 'manuscript_id',
        type: 'string',
        description: 'Document ID containing the manuscript to verify',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Manuscript ID cannot be empty';
          }
          return null;
        }
      },
      {
        name: 'scope',
        type: 'string',
        description: 'Verification scope: "all" (default), "suspicious" (heuristics), or specific citation numbers like "12,15,27"',
        required: false,
        defaultValue: 'all'
      },
      {
        name: 'import_papers',
        type: 'boolean',
        description: 'Import fetched papers into dataspace (creates folder "Citations from [Manuscript]")',
        required: false,
        defaultValue: true
      },
      {
        name: 'confidence_threshold',
        type: 'number',
        description: 'Minimum confidence (0-100) to flag an issue. Lower = more sensitive. Default: 70',
        required: false,
        defaultValue: 70
      }
    ],
    contextRequirements: {
      requiresActiveWindow: false,
      requiresSelectedText: false,
      requiresEntity: true
    }
  },
  
  examples: {
    good: [
      {
        input: {
          manuscript_id: 'doc_research_paper_123',
          scope: 'all'
        },
        description: 'Verify all citations in a manuscript. The tool reads the document internally.',
        expectedOutput: createSuccessResult(
          'Verified 47 citations',
          { totalCitations: 47, issuesFound: 3 }
        )
      },
      {
        input: {
          manuscript_id: 'doc_thesis_456',
          scope: '12,15,27',
          import_papers: false
        },
        description: 'Check specific citations without importing papers into the dataspace',
        expectedOutput: createSuccessResult(
          'Verified 3 citations',
          { totalCitations: 3, issuesFound: 0 }
        )
      }
    ],
    bad: [
      {
        input: { manuscript_id: '' },
        error: 'Manuscript ID cannot be empty',
        why: 'Missing required manuscript ID',
        fix: 'Provide a valid manuscript entity ID'
      }
    ]
  },
  
  performance: {
    expectedDuration: 300000, // 5 minutes for ~50 citations
    complexity: 'high',
    memoryIntensive: true
  },
  
  handler: async (params: VerifyCitationsParams, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    console.log('[verify_citations] ========== HANDLER STARTED ==========');
    console.log('[verify_citations] Params:', params);
    console.log('[verify_citations] Context keys:', Object.keys(context));
    
    log.info('[verify_citations] Starting verification:', {
      manuscriptId: params.manuscript_id,
      scope: params.scope || 'all'
    });
    
    // Create cancellable context - listens for abort events automatically
    const cancellable = createCancellable(context.signal);
    
    try {
      // Check if already cancelled before starting
      cancellable.checkPoint();
      // Step 1: Read the document content using doc_read_v2
      console.log('[verify_citations] Step 1: Reading document content');
      
      const { useCommandRegistryStore } = await import('../../commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      
      // Wrap with cancellation support
      const readResult = await cancellable.wrap(
        executeCommand('doc_read_v2', {
          id: params.manuscript_id,
          selector: { all: true }
        }, context)
      ) as StandardToolResult;
      
      if (readResult.status !== 'success' || !readResult.data?.markdown) {
        console.error('[verify_citations] Failed to read document:', readResult);
        return createErrorResult(
          'Failed to read document',
          (readResult as any).message || 'Could not read document content',
          'execution'
        );
      }
      
      const manuscriptText = (readResult.data as any).markdown;
      const manuscriptName = (readResult.data as any).metadata?.documentName || (readResult.data as any).documentName || 'Untitled Manuscript';
      
      console.log('[verify_citations] Document read successfully:', {
        manuscriptName,
        textLength: manuscriptText.length,
        wordCount: manuscriptText.split(/\s+/).length
      });
      
      // Phase 1: Extract citations
      reportProgress(context, {
        phase: 'extracting',
        percentage: 10,
        stats: {}
      });
      
      const extractions = await cancellable.wrap(
        extractCitations(manuscriptText, params.manuscript_id, context)
      );
      
      log.info('[verify_citations] Extraction complete:', {
        citationsFound: extractions.inTextCitations.length,
        referencesFound: extractions.references.length,
        hasFormalReferences: extractions.references.length > 0,
        resolutionStrategy: extractions.references.length > 0 ? 'formal_references' : 'direct_citation_metadata'
      });
      
      // Filter by scope if needed
      let citationsToVerify = extractions.inTextCitations;
      if (params.scope && params.scope !== 'all') {
        const scopeNumbers = parseCitationScope(params.scope);
        if (scopeNumbers) {
          citationsToVerify = extractions.inTextCitations.filter((c, idx) => 
            scopeNumbers.includes(idx + 1)
          );
        }
      }
      
      // Phase 2: Resolve references or citations
      reportProgress(context, {
        phase: 'resolving',
        percentage: 30,
        stats: { totalCitations: citationsToVerify.length }
      });
      
      let resolutions: ResolutionResult[];
      
      // If we have formal references, resolve those
      // Otherwise, resolve citations directly using their metadata
      if (extractions.references.length > 0) {
        log.info('[verify_citations] Resolving formal references:', { count: extractions.references.length });
        const { resolveReferences } = await import('./resolver');
        resolutions = await cancellable.wrap(
          resolveReferences(
            extractions.references,
            context,
            (progress) => {
              reportProgress(context, {
                phase: 'resolving',
                percentage: 30 + (progress.completed / progress.total) * 20,
                stats: {
                  totalCitations: citationsToVerify.length,
                  processedCitations: progress.completed
                }
              });
            }
          )
        );
      } else {
        log.info('[verify_citations] No formal references section, resolving citations directly:', { 
          count: citationsToVerify.length 
        });
        const { resolveCitations } = await import('./resolver');
        const { parseCitationKey } = await import('./utils');
        
        // Extract metadata from citation keys for resolution
        const citationsForResolution = citationsToVerify.map(cit => {
          const parsed = parseCitationKey(cit.citationKey);
          log.info('[verify_citations] Parsed citation:', {
            citationKey: cit.citationKey,
            authors: parsed.authors,
            year: parsed.year,
            hasClaimText: !!cit.claimText
          });
          return {
            citationKey: cit.citationKey,
            authors: parsed.authors,
            year: parsed.year,
            claimText: cit.claimText
          };
        });
        
        resolutions = await cancellable.wrap(
          resolveCitations(
            citationsForResolution,
            context,
            (progress) => {
              reportProgress(context, {
                phase: 'resolving',
                percentage: 30 + (progress.completed / progress.total) * 20,
                stats: {
                  totalCitations: citationsToVerify.length,
                  processedCitations: progress.completed
                }
              });
            }
          )
        );
        
        // Link citations to their synthetic references
        // The resolver already created synthetic references for each resolution
        for (let i = 0; i < citationsToVerify.length; i++) {
          const citation = citationsToVerify[i];
          const resolution = resolutions[i];
          
          // Link citation to the synthetic reference created by resolver
          citation.linkedReferenceIds = [resolution.reference.id];
        }
        
        log.info('[verify_citations] Linked citations to synthetic references:', {
          count: citationsToVerify.length
        });
      }
      
      log.info('[verify_citations] Resolution complete:', {
        resolved: resolutions.filter(r => r.status === 'resolved').length,
        unresolved: resolutions.filter(r => r.status === 'unresolved').length,
        strategy: extractions.references.length > 0 ? 'formal_references' : 'direct_citation_metadata'
      });
      
      // Phase 3: Fetch papers
      reportProgress(context, {
        phase: 'fetching',
        percentage: 50,
        stats: {}
      });
      
      const fetchResults = await cancellable.wrap(
        fetchAndImportPapers(
          resolutions,
          manuscriptName,
          context,
          (progress) => {
            const foundLocally = resolutions.filter(r => r.localEntityId).length;
            const fetchingExternal = progress.total - foundLocally;
            
            reportProgress(context, {
              phase: 'fetching',
              percentage: 50 + (progress.completed / progress.total) * 20,
              stats: {
                foundLocally,
                fetchingExternal,
                failed: resolutions.filter(r => r.status === 'unresolved').length
              }
            });
          }
        )
      );
      
      log.info('[verify_citations] Fetch complete:', {
        fullText: fetchResults.filter(f => f.status === 'full_text_imported').length,
        abstractOnly: fetchResults.filter(f => f.status === 'abstract_only').length,
        failed: fetchResults.filter(f => f.status === 'failed').length
      });
      
      // Phase 4: Verify claims
      reportProgress(context, {
        phase: 'verifying',
        percentage: 70,
        stats: {}
      });
      
      const verifications = await cancellable.wrap(
        verifyCitations(
          citationsToVerify,
          fetchResults,
          context,
          (progress) => {
            reportProgress(context, {
              phase: 'verifying',
              percentage: 70 + (progress.completed / progress.total) * 20,
              stats: {
                totalCitations: citationsToVerify.length,
                processedCitations: progress.completed
              }
            });
          }
        )
      );
      
      log.info('[verify_citations] Verification complete:', {
        stronglySupports: verifications.filter(v => v.verdict === 'strongly_supports').length,
        supports: verifications.filter(v => v.verdict === 'supports').length,
        issues: verifications.filter(v => 
          v.verdict === 'contradicts' || 
          v.verdict === 'overstated_strength' ||
          v.verdict === 'overstated_generalizability'
        ).length
      });
      
      // Phase 4.5: Create inline comments at citation locations
      log.info('[verify_citations] Creating inline comments...');
      reportProgress(context, {
        phase: 'reporting',
        percentage: 90,
        stats: { processedCitations: verifications.length }
      });
      
      const commentThreadIds = await cancellable.wrap(
        createVerificationComments(params.manuscript_id, verifications, context)
      );
      
      log.info('[verify_citations] Comments created:', {
        commentCount: commentThreadIds.size,
        totalCitations: verifications.length
      });
      
      // Phase 5: Generate report
      reportProgress(context, {
        phase: 'reporting',
        percentage: 95,
        stats: {}
      });
      
      const executionTime = Date.now() - startTime;
      
      const statistics = {
        averageConfidence: verifications.reduce((sum, v) => sum + v.confidence, 0) / verifications.length,
        papersFetched: fetchResults.filter(f => f.status === 'full_text_imported' || f.status === 'abstract_only').length,
        papersFromLocalDataspace: fetchResults.filter(f => f.fetchSource === 'local_dataspace').length,
        fullTextAvailable: fetchResults.filter(f => f.status === 'full_text_imported').length,
        abstractOnly: fetchResults.filter(f => f.status === 'abstract_only').length,
        processingTime: executionTime
      };
      
      const { reportId, markdownReportId } = await cancellable.wrap(
        generateReport(
          params.manuscript_id,
          manuscriptName,
          extractions,
          resolutions,
          fetchResults,
          verifications,
          commentThreadIds,
          statistics,
          context
        )
      );
      
      // Count issues by priority
      const issuesByPriority = {
        critical: verifications.filter(v => v.verdict === 'contradicts').length,
        high: verifications.filter(v => 
          v.verdict === 'overstated_strength' || 
          v.verdict === 'overstated_generalizability'
        ).length,
        medium: verifications.filter(v => v.verdict === 'weak_support').length
      };
      
      // Get top 3 issues
      const topIssues = verifications
        .filter(v => 
          v.verdict === 'contradicts' || 
          v.verdict === 'overstated_strength' ||
          v.verdict === 'overstated_generalizability'
        )
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 3)
        .map(v => ({
          citationKey: v.citation.citationKey,
          verdict: v.verdict,
          message: v.explanation
        }));
      
      // Trace successful completion
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_complete',
        data: {
          commandId: 'verify_citations',
          success: true,
          citationsVerified: verifications.length,
          issuesFound: issuesByPriority.critical + issuesByPriority.high
        },
        metadata: { executionTime }
      });
      
      // Calculate support counts
      const supportCounts = {
        strongly_supports: verifications.filter(v => v.verdict === 'strongly_supports').length,
        supports: verifications.filter(v => v.verdict === 'supports').length,
        weak_support: verifications.filter(v => v.verdict === 'weak_support').length,
        not_addressed: verifications.filter(v => v.verdict === 'not_addressed').length,
        contradicts: verifications.filter(v => v.verdict === 'contradicts').length,
        overstated_strength: verifications.filter(v => v.verdict === 'overstated_strength').length,
        overstated_generalizability: verifications.filter(v => v.verdict === 'overstated_generalizability').length,
        cherry_picked: verifications.filter(v => v.verdict === 'cherry_picked').length
      };
      
      // Build citation details for renderer
      // Truncate to avoid overwhelming Claude API with large tool results
      const citationDetails = verifications.map(v => ({
        citationKey: v.citation.citationKey,
        claimText: v.citation.claimText.substring(0, 300), // Truncate long claims
        verdict: v.verdict,
        confidence: v.confidence,
        explanation: v.explanation?.substring(0, 500), // Truncate long explanations
        suggestedRevision: v.suggestedRevision?.substring(0, 300),
        hasIssue: ['contradicts', 'overstated_strength', 'overstated_generalizability', 'weak_support', 'cherry_picked', 'error'].includes(v.verdict),
        resolvedPaper: v.resolvedPaper ? {
          title: v.resolvedPaper.title.substring(0, 200),
          authors: v.resolvedPaper.authors.slice(0, 3).map(a => a.name), // Max 3 authors
          year: v.resolvedPaper.year
          // Omit abstract and other verbose fields to keep result compact
        } : undefined
      }));
      
      return createSuccessResult(
        `Verified ${verifications.length} citations`,
        {
          summary: {
            totalCitations: citationsToVerify.length,
            supportCounts,
            highPriorityIssues: issuesByPriority.critical + issuesByPriority.high
          },
          citations: citationDetails,
          topIssues,
          reportId,
          reportName: `Citation Verification - ${manuscriptName}`,
          markdownReportId,
          statistics
        },
        {
          format: 'json',
          metadata: {
            executionTime,
            affectedEntities: [params.manuscript_id, reportId, markdownReportId]
          }
        }
      );
      
    } catch (error: any) {
      // Handle cancellation separately
      if (error instanceof CancellationError) {
        log.info('[verify_citations] Cancelled by user');
        
        traceService.log({
          traceId: context.executionId,
          type: 'ai_command_cancelled',
          data: {
            commandId: 'verify_citations',
            success: false,
            reason: 'User cancelled operation'
          },
          metadata: { executionTime: Date.now() - startTime }
        });
        
        return createErrorResult(
          'Citation verification cancelled',
          'Operation cancelled by user. No changes were made to your dataspace.',
          'cancelled',
          { metadata: { executionTime: Date.now() - startTime } }
        );
      }
      
      // Handle other errors
      log.error('[verify_citations] Error:', error);
      
      // Trace error
      traceService.log({
        traceId: context.executionId,
        type: 'ai_command_error',
        data: {
          commandId: 'verify_citations',
          success: false,
          error: error instanceof Error ? error.message : String(error)
        },
        metadata: { executionTime: Date.now() - startTime }
      });
      
      return createErrorResult(
        'Citation verification failed',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * Report progress to UI
 */
function reportProgress(context: CommandExecutionContext, progress: VerificationProgress) {
  if (context.onProgress) {
    const activityMessage = `Phase ${getPhaseNumber(progress.phase)}/5: ${getPhaseLabel(progress.phase)}`;
    context.onProgress({
      type: 'streaming_update',
      toolUseId: context.executionId,
      activity: activityMessage,
      message: activityMessage, // Also include as message for backward compatibility
      progress: {
        ...progress,
        // Ensure percentage is always a number
        percentage: typeof progress.percentage === 'number' ? progress.percentage : 0
      }
    });
  }
}

/**
 * Get phase number for display
 */
function getPhaseNumber(phase: string): number {
  const phaseMap: Record<string, number> = {
    extracting: 1,
    resolving: 2,
    fetching: 3,
    verifying: 4,
    reporting: 5
  };
  return phaseMap[phase] || 1;
}

/**
 * Get phase label for display
 */
function getPhaseLabel(phase: string): string {
  const labelMap: Record<string, string> = {
    extracting: 'Extracting citations',
    resolving: 'Resolving references',
    fetching: 'Fetching papers',
    verifying: 'Verifying claims',
    reporting: 'Generating report'
  };
  return labelMap[phase] || 'Processing';
}

